import React, { useEffect, useMemo, useState } from 'react';
import {
    CCardList,
    DateRange,
    DBlock,
    DDateRangePicker,
    DIdAutocomplete,
    useCardListSort,
    usePaginator,
} from '../../../../../dum';
import {
    ColumnOrder,
    OrderQuotationsPageQuery,
    OrderQuotationsSortableFields,
    RoleId,
    useCurrentUserQuery,
    useOrderQuotationsPageLazyQuery,
} from '../../../../../../services';
import {
    SAcceptOrderQuotationDialog,
    SDeleteOrderQuotationDialog,
    SLinkOrderQuotationProductDialog,
    SUpdateOrderQuotationStatusDialog,
    SUpsertOrderQuotationDialog,
} from '../../../../../smart';
import {
    COLUMN_LABELS,
    DATE_FORMAT,
    shortLabel,
    useDebounce,
    useNumber,
    useString,
} from '../../../../../../helpers';
import dayjs from 'dayjs';
import { OrderQuotationCard } from './order-quotation-card';

type OrderQuotationDoc =
    OrderQuotationsPageQuery['paginatedOrderQuotations']['docs'][number];

interface IOrderQuotationsListBlock {
    // Every click creates a new numbered print request. The sequence matters:
    // selecting the same quotation twice must still re-fetch and reopen print.
    requestPrint?: (orderQuotation: OrderQuotationDoc) => void;
}

// The four seeded statuses (migration 1786060000000). There is no
// getOrderQuotationStatuses query — unlike pedidos, the quotation statuses are a
// fixed, closed set, so the filter options are a constant rather than a fetch.
// Keep in sync with order_quotation_statuses if a status is ever added.
const QUOTATION_STATUS_OPTIONS = [
    { id: 1, name: 'Borrador' },
    { id: 2, name: 'Enviada' },
    { id: 3, name: 'Aceptada' },
    { id: 4, name: 'Rechazada' },
];

export function OrderQuotationsListBlock({
    requestPrint,
}: IOrderQuotationsListBlock) {
    const [query, { data, previousData, loading, refetch }] =
        useOrderQuotationsPageLazyQuery();
    const [upsertOpen, setUpsertOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [statusOpen, setStatusOpen] = useState(false);
    const [acceptOpen, setAcceptOpen] = useState(false);
    const [linkOpen, setLinkOpen] = useState(false);
    const [orderQuotationId, setOrderQuotationId] = useState<null | number>(
        null,
    );
    const [statusId, setStatusId] = useState<null | number>(null);

    // Status changes AND acceptance are admin-only (Super / General), matching
    // the backend updateOrderQuotationStatus / acceptOrderQuotation gates.
    // Non-admins never see either context item. Accepting writes the client's
    // price catalog, which is why it sits behind the same gate as a manual
    // catalog edit.
    const { data: currentUserData } = useCurrentUserQuery();
    const isAdmin =
        !!currentUserData &&
        [RoleId.Super, RoleId.Admin].some((roleId) =>
            currentUserData.currentUser.role_ids.includes(roleId),
        );
    // Creating/editing drafts and linking free lines is for sales users.
    // Asistente Ventas is read-only, so it is deliberately excluded.
    const isSales =
        !!currentUserData &&
        [RoleId.Super, RoleId.Admin, RoleId.Sales].some((roleId) =>
            currentUserData.currentUser.role_ids.includes(roleId),
        );

    // Typed sort: chips and query share the generated enum (no index mapping).
    const { sort, toggle: toggleSort } =
        useCardListSort<OrderQuotationsSortableFields>(
            OrderQuotationsListBlock.name,
        );

    const [orderQuotationStatusId, setOrderQuotationStatusId] = useNumber({
        KEY: OrderQuotationsListBlock.name + '_OrderQuotationStatusId',
    });

    const [accountId, setAccountId] = useNumber({
        KEY: OrderQuotationsListBlock.name + '_AccountId',
    });

    const [filter, setFilter] = useString({
        KEY: OrderQuotationsListBlock.name + '_Filter',
    });

    const debouncedFilter = useDebounce(filter, 400);

    const [startDate, setStartDate] = useString({
        KEY: OrderQuotationsListBlock.name + '_StartDate',
    });

    const [endDate, setEndDate] = useString({
        KEY: OrderQuotationsListBlock.name + '_EndDate',
    });

    const { take, setSkip, skip } = usePaginator(
        {
            KEY: OrderQuotationsListBlock.name + '_Paginator',
        },
        [
            startDate,
            endDate,
            debouncedFilter,
            accountId,
            orderQuotationStatusId,
        ],
    );

    useEffect(() => {
        void query({
            variables: {
                skip,
                take,
                startDate: startDate || null,
                endDate: endDate || null,
                accountId,
                filter: debouncedFilter,
                sortField: sort?.field ?? null,
                sortOrder: sort
                    ? sort.order === 'asc'
                        ? ColumnOrder.Asc
                        : ColumnOrder.Desc
                    : null,
                orderQuotationStatusId,
            },
        });
    }, [
        query,
        skip,
        take,
        startDate,
        endDate,
        debouncedFilter,
        sort,
        accountId,
        orderQuotationStatusId,
    ]);

    const count =
        data && data?.paginatedOrderQuotations.count >= 0
            ? data?.paginatedOrderQuotations.count
            : previousData?.paginatedOrderQuotations.count;

    const orderQuotations: OrderQuotationsPageQuery['paginatedOrderQuotations']['docs'] =
        data?.paginatedOrderQuotations.docs ||
        previousData?.paginatedOrderQuotations.docs ||
        [];

    const handleEditOrderQuotation = useMemo(() => {
        return (orderQuotation: OrderQuotationDoc) => {
            if (!orderQuotation.is_editable) return;
            setOrderQuotationId(orderQuotation.id);
            setUpsertOpen(true);
        };
    }, []);

    const handleDeleteOrderQuotation = useMemo(() => {
        return (orderQuotation: OrderQuotationDoc) => {
            setOrderQuotationId(orderQuotation.id);
            setDeleteOpen(true);
        };
    }, []);

    const handleChangeStatus = useMemo(() => {
        return (orderQuotation: OrderQuotationDoc) => {
            setOrderQuotationId(orderQuotation.id);
            setStatusId(orderQuotation.order_quotation_status?.id ?? null);
            setStatusOpen(true);
        };
    }, []);

    const handleAccept = useMemo(() => {
        return (orderQuotation: OrderQuotationDoc) => {
            setOrderQuotationId(orderQuotation.id);
            setAcceptOpen(true);
        };
    }, []);

    const handleLinkProduct = useMemo(() => {
        return (orderQuotation: OrderQuotationDoc) => {
            setOrderQuotationId(orderQuotation.id);
            setLinkOpen(true);
        };
    }, []);

    const handlePrint = useMemo(() => {
        return (orderQuotation: OrderQuotationDoc) =>
            requestPrint?.(orderQuotation);
    }, [requestPrint]);

    return (
        <DBlock>
            <CCardList<OrderQuotationDoc, OrderQuotationsSortableFields>
                title={'Cotizaciones'}
                data={orderQuotations}
                loading={loading}
                keyOf={(orderQuotation) => orderQuotation.id}
                renderItem={(orderQuotation) => (
                    <OrderQuotationCard
                        orderQuotation={orderQuotation}
                        onEdit={isSales ? handleEditOrderQuotation : undefined}
                        onDelete={
                            isAdmin ? handleDeleteOrderQuotation : undefined
                        }
                        onChangeStatus={
                            isAdmin ? handleChangeStatus : undefined
                        }
                        onAccept={isAdmin ? handleAccept : undefined}
                        onLinkProduct={isSales ? handleLinkProduct : undefined}
                        onPrint={handlePrint}
                    />
                )}
                skip={skip}
                take={take}
                count={count}
                setSkip={setSkip}
                filterValue={filter}
                onFilterChange={setFilter}
                filterHelperText={
                    'Folio de la cotización, cliente, producto, especificación o notas.'
                }
                activeFilterCount={
                    (startDate && endDate ? 1 : 0) +
                    (orderQuotationStatusId != null ? 1 : 0) +
                    (accountId != null ? 1 : 0)
                }
                filters={
                    <>
                        <DDateRangePicker
                            label={'Fecha'}
                            value={
                                startDate && endDate
                                    ? [dayjs(startDate), dayjs(endDate)]
                                    : undefined
                            }
                            onChange={(range: DateRange) => {
                                setStartDate(range[0].format(DATE_FORMAT));
                                setEndDate(range[1].format(DATE_FORMAT));
                            }}
                            onClear={() => {
                                setStartDate('');
                                setEndDate('');
                            }}
                            disableGutters
                        />
                        <DIdAutocomplete
                            id={orderQuotationStatusId}
                            onChange={setOrderQuotationStatusId}
                            itemText={'name'}
                            label={'Estatus'}
                            options={QUOTATION_STATUS_OPTIONS}
                            disableGutters
                        />
                        <DIdAutocomplete
                            id={accountId}
                            onChange={setAccountId}
                            itemText={'name'}
                            label={'Cliente'}
                            options={data?.getAccounts || []}
                            disableGutters
                        />
                    </>
                }
                sortOptions={[
                    {
                        label: 'Código',
                        field: OrderQuotationsSortableFields.OrderCode,
                    },
                    {
                        label: 'Fecha',
                        field: OrderQuotationsSortableFields.Date,
                    },
                    {
                        label: 'Entrega estimada',
                        field: OrderQuotationsSortableFields.EstimatedDeliveryDate,
                    },
                    {
                        label: 'Vigencia',
                        field: OrderQuotationsSortableFields.ExpirationDate,
                    },
                ]}
                sort={sort}
                onSortToggle={toggleSort}
                onItemDoubleClick={
                    isSales ? handleEditOrderQuotation : undefined
                }
                contextItems={[
                    ...(isSales
                        ? [
                              {
                                  title: shortLabel(COLUMN_LABELS.edit),
                                  disable: (
                                      orderQuotation?: OrderQuotationDoc,
                                  ) => !orderQuotation?.is_editable,
                                  callback: (
                                      orderQuotation: OrderQuotationDoc,
                                  ) => handleEditOrderQuotation(orderQuotation),
                              },
                          ]
                        : []),
                    ...(isAdmin
                        ? [
                              {
                                  title: shortLabel(COLUMN_LABELS.delete),
                                  disable: (
                                      orderQuotation?: OrderQuotationDoc,
                                  ) => !orderQuotation?.is_deletable,
                                  callback: (
                                      orderQuotation: OrderQuotationDoc,
                                  ) =>
                                      handleDeleteOrderQuotation(
                                          orderQuotation,
                                      ),
                              },
                          ]
                        : []),
                    {
                        // Read-only; available to every viewer of the page.
                        title: shortLabel(COLUMN_LABELS.print_pdf),
                        callback: (orderQuotation) =>
                            handlePrint(orderQuotation),
                    },
                    ...(isAdmin
                        ? [
                              {
                                  title: 'Aceptación',
                                  // Enviada opens the actionable acceptance
                                  // flow; Aceptada opens the same dialog as a
                                  // read-only historical preview.
                                  disable: (
                                      orderQuotation?: OrderQuotationDoc,
                                  ) =>
                                      ![2, 3].includes(
                                          orderQuotation?.order_quotation_status
                                              ?.id ?? 0,
                                      ),
                                  callback: (
                                      orderQuotation: OrderQuotationDoc,
                                  ) => handleAccept(orderQuotation),
                              },
                              {
                                  title: shortLabel(
                                      COLUMN_LABELS.change_status,
                                  ),
                                  // Locked once acceptance starts (catalog
                                  // written or completed / Aceptada) — the
                                  // backend refuses any status change then.
                                  disable: (
                                      orderQuotation?: OrderQuotationDoc,
                                  ) =>
                                      !!orderQuotation?.catalog_step_done ||
                                      !!orderQuotation?.acceptance_complete ||
                                      orderQuotation?.order_quotation_status
                                          ?.id === 3,
                                  callback: (
                                      orderQuotation: OrderQuotationDoc,
                                  ) => handleChangeStatus(orderQuotation),
                              },
                          ]
                        : []),
                    ...(isSales
                        ? [
                              {
                                  title: shortLabel(COLUMN_LABELS.link_product),
                                  // Only meaningful with a free line still to
                                  // link, before acceptance starts. The backend
                                  // refuses out-of-state links; the dialog itself
                                  // shows when there is nothing left to link.
                                  disable: (
                                      orderQuotation?: OrderQuotationDoc,
                                  ) =>
                                      !orderQuotation?.order_quotation_products?.some(
                                          (orp) => orp.product_id == null,
                                      ),
                                  callback: (
                                      orderQuotation: OrderQuotationDoc,
                                  ) => handleLinkProduct(orderQuotation),
                              },
                          ]
                        : []),
                ]}
            />
            <SUpsertOrderQuotationDialog
                open={upsertOpen}
                setOpen={setUpsertOpen}
                orderQuotationId={orderQuotationId}
            />
            <SDeleteOrderQuotationDialog
                open={deleteOpen}
                setOpen={setDeleteOpen}
                orderQuotationId={orderQuotationId}
            />
            <SUpdateOrderQuotationStatusDialog
                open={statusOpen}
                setOpen={setStatusOpen}
                orderQuotationId={orderQuotationId}
                currentStatusId={statusId}
            />
            <SAcceptOrderQuotationDialog
                open={acceptOpen}
                setOpen={setAcceptOpen}
                orderQuotationId={orderQuotationId}
            />
            <SLinkOrderQuotationProductDialog
                open={linkOpen}
                setOpen={(open) => {
                    setLinkOpen(open);
                    // Linking a free line to a product mutates the quotation's
                    // lines, but the dialog closes without the list card picking
                    // up the change reliably (the "Línea libre" chip and the
                    // link action would stay stale). Refetch the list on close so
                    // the card reflects the now-linked line immediately. `data`
                    // guards against calling refetch before the query has run.
                    if (!open && data) {
                        void refetch?.();
                    }
                }}
                orderQuotationId={orderQuotationId}
            />
        </DBlock>
    );
}
